import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Building2, Download, CheckCircle, XCircle, RefreshCw, 
  FileText, AlertTriangle, ExternalLink, Loader2, Clock,
  Plus, Trash2, Copy, Send, User, Briefcase, Search,
  ChevronDown, ChevronUp, Shield, Calendar,
  Database, Receipt
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CassettoFiscalePage = ({ tenantDb: propTenantDb }) => {
  const tenantDb = propTenantDb || localStorage.getItem('tenant_db') || 'admin_cassetto';
  
  // Stati
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showFattureModal, setShowFattureModal] = useState(false);
  const [fatture, setFatture] = useState([]);
  const [loadingFatture, setLoadingFatture] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Expanded client cards
  const [expandedTenant, setExpandedTenant] = useState(null);
  
  // Form nuovo tenant
  const [newTenant, setNewTenant] = useState({
    nome: '',
    codice_fiscale: '',
    tipo: 'persona_legale',
    email: '',
    telefono: ''
  });
  const [addingTenant, setAddingTenant] = useState(false);
  
  // Azioni in corso
  const [generatingLink, setGeneratingLink] = useState(null);
  const [downloadingFatture, setDownloadingFatture] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('tenant_token');
    const db = tenantDb || localStorage.getItem('tenant_db') || 'admin_cassetto';
    return {
      'Authorization': `Bearer ${token}`,
      'X-Tenant-DB': db
    };
  };

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadStatus(), loadTenants()]);
    setLoading(false);
  };

  const loadStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/cassetto-fiscale/status`, {
        headers: getAuthHeaders()
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Errore caricamento status:', error);
      setStatus({ status: 'error', message: error.message });
    }
  };

  const loadTenants = async () => {
    try {
      const response = await axios.get(`${API}/api/cassetto-fiscale/tenants`, {
        headers: getAuthHeaders()
      });
      setTenants(response.data.tenants || []);
    } catch (error) {
      console.error('Errore caricamento tenants:', error);
    }
  };

  const handleAddTenant = async () => {
    if (!newTenant.nome || !newTenant.codice_fiscale) {
      toast.error('Inserisci nome e codice fiscale/P.IVA');
      return;
    }

    setAddingTenant(true);
    try {
      await axios.post(`${API}/api/cassetto-fiscale/tenants`, newTenant, {
        headers: getAuthHeaders()
      });
      toast.success('Cliente aggiunto con successo!');
      setShowAddModal(false);
      setNewTenant({ nome: '', codice_fiscale: '', tipo: 'persona_legale', email: '', telefono: '' });
      loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiunta cliente');
    } finally {
      setAddingTenant(false);
    }
  };

  const handleGenerateLink = async (tenant) => {
    setGeneratingLink(tenant.id);
    try {
      const response = await axios.post(
        `${API}/api/cassetto-fiscale/tenants/${tenant.id}/genera-link`,
        { tenant_id: tenant.id, anni_fatture: [2024, 2025, 2026] },
        { headers: getAuthHeaders() }
      );
      toast.success('Link SPID generato! Invialo al cliente.');
      loadTenants();
      
      if (response.data.link_url) {
        navigator.clipboard.writeText(response.data.link_url);
        toast.info('Link copiato negli appunti!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore generazione link');
    } finally {
      setGeneratingLink(null);
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copiato negli appunti!');
  };

  const handleCheckStatus = async (tenant) => {
    setCheckingStatus(tenant.id);
    try {
      const response = await axios.get(
        `${API}/api/cassetto-fiscale/tenants/${tenant.id}/stato-link`,
        { headers: getAuthHeaders() }
      );
      
      if (response.data.stato === 'autorizzato') {
        toast.success('Cliente autorizzato! Puoi scaricare le fatture.');
      } else if (response.data.stato === 'in_attesa') {
        toast.info('In attesa di autorizzazione SPID dal cliente');
      } else {
        toast.info(`Stato: ${response.data.stato}`);
      }
      loadTenants();
    } catch (error) {
      toast.error('Errore verifica stato');
    } finally {
      setCheckingStatus(null);
    }
  };

  const handleDownloadFatture = async (tenant) => {
    setDownloadingFatture(tenant.id);
    try {
      const response = await axios.post(
        `${API}/api/cassetto-fiscale/tenants/${tenant.id}/scarica-fatture`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Scaricate ${response.data.totale} fatture!`);
      loadTenants();
      // Espandi automaticamente per mostrare le fatture
      setExpandedTenant(tenant.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore download fatture');
    } finally {
      setDownloadingFatture(null);
    }
  };

  const handleViewFatture = async (tenant) => {
    setSelectedTenant(tenant);
    setShowFattureModal(true);
    setLoadingFatture(true);
    
    try {
      const response = await axios.get(
        `${API}/api/cassetto-fiscale/tenants/${tenant.id}/fatture`,
        { headers: getAuthHeaders() }
      );
      setFatture(response.data.fatture || []);
    } catch (error) {
      
    } finally {
      setLoadingFatture(false);
    }
  };

  const handleDeleteTenant = async (tenant) => {
    if (!window.confirm(`Eliminare ${tenant.nome}?`)) return;
    
    try {
      await axios.delete(
        `${API}/api/cassetto-fiscale/tenants/${tenant.id}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Cliente eliminato');
      loadTenants();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const getStatoBadge = (stato) => {
    const badges = {
      'non_autorizzato': { color: 'bg-gray-500', icon: Clock, text: 'Non autorizzato' },
      'in_attesa': { color: 'bg-amber-500', icon: Clock, text: 'In attesa SPID' },
      'autorizzato': { color: 'bg-green-500', icon: CheckCircle, text: 'Autorizzato' },
      'errore': { color: 'bg-red-500', icon: XCircle, text: 'Errore' },
      'scaricamento': { color: 'bg-blue-500 animate-pulse', icon: Download, text: 'Scaricamento...' }
    };
    const badge = badges[stato] || badges['non_autorizzato'];
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white ${badge.color}`}>
        <Icon className="h-3 w-3" />
        {badge.text}
      </span>
    );
  };

  const filteredTenants = tenants.filter(t => 
    t.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.codice_fiscale.includes(searchTerm)
  );

  // Calcolo statistiche
  const stats = {
    totaleClienti: tenants.length,
    autorizzati: tenants.filter(t => t.stato === 'autorizzato').length,
    inAttesa: tenants.filter(t => t.stato === 'in_attesa').length,
    totaleFatture: tenants.reduce((sum, t) => sum + (t.fatture_count || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white lg:text-gray-900 flex items-center gap-2">
            <Shield className="h-7 w-7 text-amber-500" />
            Cassetto Fiscale
          </h1>
          <p className="text-slate-400 lg:text-gray-600 text-sm mt-1">
            Scarica automaticamente le fatture elettroniche dei tuoi clienti
          </p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          {/* Status FiscoAPI */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
            status?.status === 'connected' 
              ? 'bg-green-500/20 text-green-400 lg:bg-green-100 lg:text-green-700' 
              : 'bg-red-500/20 text-red-400 lg:bg-red-100 lg:text-red-700'
          }`}>
            {status?.status === 'connected' ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            FiscoAPI {status?.status === 'connected' ? 'Connesso' : 'Disconnesso'}
          </div>
          
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Plus className="h-4 w-4 mr-1" />
            Aggiungi Cliente
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 lg:bg-white rounded-xl border border-slate-700 lg:border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Building2 className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white lg:text-gray-900">{stats.totaleClienti}</p>
              <p className="text-xs text-slate-400 lg:text-gray-500">Clienti Totali</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 lg:bg-white rounded-xl border border-slate-700 lg:border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white lg:text-gray-900">{stats.autorizzati}</p>
              <p className="text-xs text-slate-400 lg:text-gray-500">Autorizzati</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 lg:bg-white rounded-xl border border-slate-700 lg:border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Clock className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white lg:text-gray-900">{stats.inAttesa}</p>
              <p className="text-xs text-slate-400 lg:text-gray-500">In Attesa</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 lg:bg-white rounded-xl border border-slate-700 lg:border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Receipt className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white lg:text-gray-900">{stats.totaleFatture}</p>
              <p className="text-xs text-slate-400 lg:text-gray-500">Fatture Scaricate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Cerca cliente per nome o codice fiscale..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-slate-800/50 lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
        />
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <h3 className="font-semibold text-blue-400 lg:text-blue-600 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Come funziona
        </h3>
        <ol className="mt-2 text-sm text-blue-300/80 lg:text-blue-600/80 space-y-1">
          <li>1. Aggiungi un cliente con il suo Codice Fiscale o P.IVA</li>
          <li>2. Genera il link SPID e invialo al cliente (WhatsApp, Email)</li>
          <li>3. Il cliente clicca e autorizza con SPID (1 minuto)</li>
          <li>4. Le fatture vengono scaricate e salvate nel database!</li>
        </ol>
      </div>

      {/* Tenants List */}
      {filteredTenants.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/30 lg:bg-gray-50 rounded-xl">
          <Building2 className="h-12 w-12 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-400 lg:text-gray-600">
            {searchTerm ? 'Nessun cliente trovato' : 'Nessun cliente registrato'}
          </p>
          <Button
            onClick={() => setShowAddModal(true)}
            className="mt-4 bg-amber-600 hover:bg-amber-700"
          >
            <Plus className="h-4 w-4 mr-1" />
            Aggiungi il primo cliente
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredTenants.map((tenant) => (
            <div
              key={tenant.id}
              className="bg-slate-800/50 lg:bg-white rounded-xl border border-slate-700 lg:border-gray-200 overflow-hidden"
            >
              {/* Main Row */}
              <div className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Info */}
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/20">
                      {tenant.tipo === 'persona_legale' ? (
                        <Briefcase className="h-5 w-5 text-amber-400" />
                      ) : (
                        <User className="h-5 w-5 text-amber-400" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white lg:text-gray-900">{tenant.nome}</h3>
                      <p className="text-sm text-slate-400 lg:text-gray-600">
                        {tenant.tipo === 'persona_legale' ? 'P.IVA' : 'CF'}: {tenant.codice_fiscale}
                      </p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {getStatoBadge(downloadingFatture === tenant.id ? 'scaricamento' : tenant.stato)}
                        {tenant.fatture_count > 0 && (
                          <button
                            onClick={() => setExpandedTenant(expandedTenant === tenant.id ? null : tenant.id)}
                            className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"
                          >
                            <Database className="h-3 w-3" />
                            {tenant.fatture_count} fatture salvate
                            {expandedTenant === tenant.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          </button>
                        )}
                        {tenant.ultima_sincronizzazione && (
                          <span className="text-xs text-slate-400 lg:text-gray-500">
                            <Calendar className="h-3 w-3 inline mr-1" />
                            Ultimo sync: {new Date(tenant.ultima_sincronizzazione).toLocaleDateString('it-IT')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {tenant.stato === 'non_autorizzato' && (
                      <Button
                        size="sm"
                        onClick={() => handleGenerateLink(tenant)}
                        disabled={generatingLink === tenant.id}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {generatingLink === tenant.id ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <Send className="h-4 w-4 mr-1" />
                        )}
                        Genera Link SPID
                      </Button>
                    )}

                    {tenant.stato === 'in_attesa' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCopyLink(tenant.link_url)}
                          className="border-slate-600 lg:border-gray-300"
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copia Link
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCheckStatus(tenant)}
                          disabled={checkingStatus === tenant.id}
                          className="border-slate-600 lg:border-gray-300"
                        >
                          {checkingStatus === tenant.id ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-1" />
                          )}
                          Verifica Stato
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => window.open(tenant.link_url, '_blank')}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Apri Link
                        </Button>
                      </>
                    )}

                    {tenant.stato === 'autorizzato' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleDownloadFatture(tenant)}
                          disabled={downloadingFatture === tenant.id}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {downloadingFatture === tenant.id ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              Scaricamento...
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4 mr-1" />
                              Scarica Fatture
                            </>
                          )}
                        </Button>
                        {tenant.fatture_count > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewFatture(tenant)}
                            className="border-slate-600 lg:border-gray-300"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Vedi Fatture
                          </Button>
                        )}
                      </>
                    )}

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteTenant(tenant)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Expanded Fatture Preview */}
              {expandedTenant === tenant.id && tenant.fatture_count > 0 && (
                <div className="border-t border-slate-700 lg:border-gray-200 bg-slate-900/50 lg:bg-gray-50 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-white lg:text-gray-900 flex items-center gap-2">
                      <Database className="h-4 w-4 text-amber-500" />
                      Fatture Salvate nel Database
                    </h4>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewFatture(tenant)}
                      className="text-xs"
                    >
                      Vedi Tutte
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 lg:text-gray-500 mb-2">
                    Le fatture sono salvate nella collection <code className="bg-slate-800 lg:bg-gray-200 px-1 rounded">fatture_cassetto</code> del database
                  </p>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
                    <div className="bg-slate-800 lg:bg-white p-2 rounded">
                      <span className="text-slate-400 lg:text-gray-500">Totale:</span>
                      <span className="ml-1 text-white lg:text-gray-900 font-semibold">{tenant.fatture_count}</span>
                    </div>
                    <div className="bg-slate-800 lg:bg-white p-2 rounded">
                      <span className="text-slate-400 lg:text-gray-500">Ultimo sync:</span>
                      <span className="ml-1 text-white lg:text-gray-900 font-semibold">
                        {tenant.ultima_sincronizzazione 
                          ? new Date(tenant.ultima_sincronizzazione).toLocaleDateString('it-IT')
                          : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal Aggiungi Cliente */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 lg:bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white lg:text-gray-900 mb-4">
              Aggiungi Cliente
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-1">
                  Nome / Ragione Sociale *
                </label>
                <Input
                  value={newTenant.nome}
                  onChange={(e) => setNewTenant({ ...newTenant, nome: e.target.value })}
                  placeholder="Es. Ristorante da Mario"
                  className="bg-slate-800 lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-1">
                  Codice Fiscale / P.IVA *
                </label>
                <Input
                  value={newTenant.codice_fiscale}
                  onChange={(e) => setNewTenant({ ...newTenant, codice_fiscale: e.target.value.toUpperCase().replace(/^IT/, '') })}
                  placeholder="Es. 12345678901 (senza IT)"
                  className="bg-slate-800 lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Inserisci solo le cifre, senza prefisso "IT"
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-1">
                  Tipo
                </label>
                <select
                  value={newTenant.tipo}
                  onChange={(e) => setNewTenant({ ...newTenant, tipo: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 lg:bg-white border border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                >
                  <option value="persona_legale">Persona Giuridica (Azienda)</option>
                  <option value="persona_fisica">Persona Fisica</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-1">
                  Email (opzionale)
                </label>
                <Input
                  type="email"
                  value={newTenant.email}
                  onChange={(e) => setNewTenant({ ...newTenant, email: e.target.value })}
                  placeholder="email@esempio.it"
                  className="bg-slate-800 lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowAddModal(false)}
                className="border-slate-600 lg:border-gray-300"
              >
                Annulla
              </Button>
              <Button
                onClick={handleAddTenant}
                disabled={addingTenant}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {addingTenant ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Plus className="h-4 w-4 mr-1" />
                )}
                Aggiungi
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Visualizza Fatture */}
      {showFattureModal && selectedTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 lg:bg-white rounded-xl p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white lg:text-gray-900">
                Fatture - {selectedTenant.nome}
              </h2>
              <Button
                variant="ghost"
                onClick={() => setShowFattureModal(false)}
                className="text-slate-400"
              >
                ✕
              </Button>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
              <p className="text-xs text-blue-400 lg:text-blue-600">
                <Database className="h-4 w-4 inline mr-1" />
                Queste fatture sono salvate nel database nella collection <code className="bg-slate-800 lg:bg-gray-200 px-1 rounded">fatture_cassetto</code>
              </p>
            </div>

            <div className="flex-1 overflow-auto">
              {loadingFatture ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
                </div>
              ) : fatture.length === 0 ? (
                <p className="text-center text-slate-400 py-8">Nessuna fattura trovata</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-800 lg:bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-slate-300 lg:text-gray-700">Numero</th>
                      <th className="px-4 py-2 text-left text-slate-300 lg:text-gray-700">Data</th>
                      <th className="px-4 py-2 text-left text-slate-300 lg:text-gray-700">Tipo</th>
                      <th className="px-4 py-2 text-left text-slate-300 lg:text-gray-700">Fornitore/Cliente</th>
                      <th className="px-4 py-2 text-right text-slate-300 lg:text-gray-700">Imponibile</th>
                      <th className="px-4 py-2 text-right text-slate-300 lg:text-gray-700">IVA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fatture.map((ft, idx) => (
                      <tr key={idx} className="border-b border-slate-700 lg:border-gray-200">
                        <td className="px-4 py-2 text-white lg:text-gray-900">{ft.numero}</td>
                        <td className="px-4 py-2 text-slate-300 lg:text-gray-600">{ft.data}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            ft.tipo === 'ricevuta' 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : 'bg-green-500/20 text-green-400'
                          }`}>
                            {ft.tipo === 'ricevuta' ? 'Ricevuta' : 'Emessa'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-white lg:text-gray-900">
                          {ft.fornitore || ft.cliente}
                        </td>
                        <td className="px-4 py-2 text-right text-white lg:text-gray-900">{ft.imponibile}</td>
                        <td className="px-4 py-2 text-right text-slate-300 lg:text-gray-600">{ft.imposta}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CassettoFiscalePage;
